/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type {
  GetCollectionOwnersViewParamsADto,
  GetCollectionsAnalyticsViewParamsADto,
  GetDigitalAssetCollectionsParamsADto,
} from '@cohort/admin-schemas/digitalAssetCollection';
import type {QueryOptions} from '@cohort/merchants/hooks/api/Query';
import {useCohortQuery} from '@cohort/merchants/hooks/api/Query';
import {
  getDigitalAssetCollection,
  getDigitalAssetCollectionOwnersView,
  getDigitalAssetCollections,
  getDigitalAssetCollectionsAnalyticsView,
} from '@cohort/merchants/lib/api/DigitalAssetCollections';

export const digitalAssetCollectionsKeys = {
  digitalAssetCollections: ['digitalAssetCollections'] as const,
  list: (merchantId: string) =>
    [...digitalAssetCollectionsKeys.digitalAssetCollections, merchantId, 'list'] as const,
  listWithFilters: (merchantId: string, params: GetDigitalAssetCollectionsParamsADto) =>
    [...digitalAssetCollectionsKeys.list(merchantId), params] as const,
  getById: (merchantId: string, digitalAssetCollectionId: string) =>
    [
      ...digitalAssetCollectionsKeys.digitalAssetCollections,
      merchantId,
      digitalAssetCollectionId,
    ] as const,
  ownersView: (
    merchantId: string,
    digitalAssetCollectionId: string,
    params: GetCollectionOwnersViewParamsADto
  ) =>
    [
      ...digitalAssetCollectionsKeys.getById(merchantId, digitalAssetCollectionId),
      'owners-view',
      params,
    ] as const,
  analyticsView: (merchantId: string, params: GetCollectionsAnalyticsViewParamsADto) =>
    [...digitalAssetCollectionsKeys.list(merchantId), 'analytics-view', params] as const,
};

export const useDigitalAssetCollection = (
  merchantId: string,
  digitalAssetCollectionId: string,
  options?: QueryOptions
) =>
  useCohortQuery({
    queryKey: digitalAssetCollectionsKeys.getById(merchantId, digitalAssetCollectionId),
    queryFn: async () => getDigitalAssetCollection(merchantId, digitalAssetCollectionId),
    ...options,
  });

export const useDigitalAssetCollections = (
  merchantId: string,
  params: GetDigitalAssetCollectionsParamsADto,
  options?: QueryOptions
) =>
  useCohortQuery({
    queryKey: digitalAssetCollectionsKeys.listWithFilters(merchantId, params),
    queryFn: async () => getDigitalAssetCollections(merchantId, params),
    ...options,
    refetchInterval: options?.autoRefresh === true ? 5000 : false,
  });

export const useDigitalAssetCollectionOwnersView = (
  merchantId: string,
  digitalAssetCollectionId: string,
  params: GetCollectionOwnersViewParamsADto,
  options?: QueryOptions
) =>
  useCohortQuery({
    queryKey: digitalAssetCollectionsKeys.ownersView(merchantId, digitalAssetCollectionId, params),
    queryFn: async () =>
      getDigitalAssetCollectionOwnersView(merchantId, digitalAssetCollectionId, params),
    ...options,
    refetchInterval: options?.autoRefresh === true ? 5000 : false,
  });

export const useDigitalAssetCollectionsAnalyticsView = (
  merchantId: string,
  params: GetCollectionsAnalyticsViewParamsADto,
  options?: QueryOptions
) =>
  useCohortQuery({
    queryKey: digitalAssetCollectionsKeys.analyticsView(merchantId, params),
    queryFn: async () => getDigitalAssetCollectionsAnalyticsView(merchantId, params),
    ...options,
    refetchInterval: options?.autoRefresh === true ? 5000 : false,
  });

export const useDigitalAssetCollectionAnalyticsView = (
  merchantId: string,
  digitalAssetCollectionId: string,
  options?: QueryOptions
) => {
  const params = {
    digitalAssetCollectionId,
    page: 1,
    pageSize: 1,
  } satisfies GetCollectionsAnalyticsViewParamsADto;

  return useCohortQuery({
    queryKey: digitalAssetCollectionsKeys.analyticsView(merchantId, params),
    queryFn: async () => getDigitalAssetCollectionsAnalyticsView(merchantId, params),
    ...options,
  });
};
